import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Heart, ShoppingBag, User, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import CartSheet from "@/components/CartSheet";
import SearchOverlay from "@/components/SearchOverlay";

const LOGO = "/yourwishlist_logo.png";

const NAV_STATIC = [
  { to: "/shop", label: "Shop All" },
  // { to: "/blog", label: "Journal" },
  // { to: "/about", label: "About" },
];

export default function Navbar() {
  const { user, cartCount, wishlist, setCartOpen, setSearchOpen } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);

    /*
     * PERFORMANCE:
     * Passive scrolling lets the browser handle scrolling without waiting
     * for this UI state listener.
     */
    window.addEventListener("scroll", on, { passive: true });

    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    api
      .get("/products/categories")
      .then((res) => {
        const categories =
          Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data.categories)
              ? res.data.categories
              : Array.isArray(res.data.data)
                ? res.data.data
                : [];

        setCats(categories);
      })
      .catch(() => {
        // Category navigation is optional. Keep the navbar usable
        // even if the category API is temporarily unavailable.
        setCats([]);
      });
  }, []);

  const safeCats = Array.isArray(cats) ? cats : [];

  const NAV = [
    NAV_STATIC[0],
    ...safeCats.map((c) => ({
      to: `/shop/${c.slug}`,
      label: c.name,
    })),
    // NAV_STATIC[1],
    // NAV_STATIC[2],
  ];

  return (
    <>
      {/* Announcement bar - intentionally kept commented so it can be enabled later. */}
      {/*
      <div className="bg-yw-ink text-white text-[12px] tracking-[0.2em] uppercase py-2 text-center font-inter">
        Buy 2 or more to get free shipping · 7-day easy returns
      </div>
      */}

      <header
        data-testid="site-navbar"
        className={`sticky top-0 z-40 h-20 bg-yw-bg transition-all duration-300 ${
          scrolled
            ? "bg-yw-bg/95 backdrop-blur-xl border-b border-yw-border"
            : "bg-yw-bg"
        }`}
      >
        {/* Fixed-height navbar prevents the page from jumping when the page/API finishes loading. */}
        <div className="relative max-w-1440 mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-3">
          {/* Left: desktop navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.slice(0, 6).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `font-inter text-[12px] tracking-[0.18em] uppercase transition-colors ${
                    isActive
                      ? "text-yw-gold"
                      : "text-yw-ink hover:text-yw-gold"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden text-yw-ink"
            aria-label="Open menu"
            data-testid="nav-mobile-toggle"
          >
            <Menu size={22} />
          </button>

          {/* Centered logo
              Absolute centering keeps the logo mathematically centered on desktop,
              independent of the width of the left/right navigation areas. */}
          <Link
            to="/"
            data-testid="nav-logo"
            aria-label="Your Wishlist home"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
          >
            <img
              src={LOGO}
              alt="Your Wishlist"
              width="224"
              height="56"
              className="block w-[224px] h-[56px] max-w-none select-none object-contain"
              decoding="async"
            />
          </Link>

          {/* Right icons */}
          <div className="ml-auto flex items-center gap-4 md:gap-6">
            <button
              onClick={() => setSearchOpen(true)}
              className="text-yw-ink hover:text-yw-gold transition-colors"
              aria-label="Search"
              data-testid="nav-search-btn"
            >
              <Search size={20} />
            </button>

            <button
              onClick={() => nav(user ? "/account" : "/login")}
              className="text-yw-ink hover:text-yw-gold transition-colors hidden sm:block"
              aria-label="Account"
              data-testid="nav-account-btn"
            >
              <User size={20} />
            </button>

            <button
              onClick={() => nav("/wishlist")}
              className="relative text-yw-ink hover:text-yw-gold transition-colors"
              aria-label="Wishlist"
              data-testid="nav-wishlist-btn"
            >
              <Heart size={20} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-yw-gold text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                  {wishlist.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setCartOpen(true)}
              className="relative text-yw-ink hover:text-yw-gold transition-colors"
              aria-label="Cart"
              data-testid="nav-cart-btn"
            >
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-yw-gold text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-50 bg-yw-bg flex flex-col"
            data-testid="nav-mobile-menu"
          >
            {/* Mobile menu header */}
            <div className="relative flex items-center justify-center h-20 px-6 border-b border-yw-border">
              {/* Clicking the side-menu logo always returns to Home. */}
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                data-testid="nav-mobile-logo"
                aria-label="Your Wishlist home"
                className="flex items-center justify-center"
              >
                <img
                  src={LOGO}
                  alt="Your Wishlist"
                  width="160"
                  height="40"
                  className="block w-[160px] h-[40px] max-w-none object-contain"
                  decoding="async"
                />
              </Link>

              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                data-testid="nav-mobile-close"
                className="absolute right-6 text-yw-ink"
              >
                <X size={22} />
              </button>
            </div>

            <nav className="flex flex-col p-8 gap-5">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="font-playfair text-[1.1rem] leading-[0.625rem] text-yw-ink hover:text-yw-gold transition-colors"
                >
                  {n.label}
                </Link>
              ))}

              <div className="border-t border-yw-border my-3" />

              <Link
                to={user ? "/account" : "/login"}
                onClick={() => setMobileOpen(false)}
                data-testid="nav-mobile-account"
                className="font-playfair text-[0.875rem] leading-[0.625rem] text-yw-ink hover:text-yw-gold transition-colors flex items-center gap-3"
              >
                <User size={18} /> {user ? "My Account" : "Sign in"}
              </Link>

              <Link
                to="/wishlist"
                onClick={() => setMobileOpen(false)}
                data-testid="nav-mobile-wishlist"
                className="font-playfair text-[0.875rem] leading-[0.625rem] text-yw-ink hover:text-yw-gold transition-colors flex items-center gap-3"
              >
                <Heart size={18} />
                Wishlist{" "}
                {wishlist.length > 0 && (
                  <span className="text-[0.75rem] leading-normal text-yw-gold">
                    ({wishlist.length})
                  </span>
                )}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <CartSheet />
      <SearchOverlay />
    </>
  );
}
